/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useId, useState, useEffect } from 'react';
import cx from 'classnames';
import Hidden from '../../Hidden';
import azCommonStyles from '../../../theme/globals.module.scss';
import Accordion from '../../Accordion/Accordion';
import AccordionSummary, {
  type AccordionSummaryProps,
} from '@/components/Accordion/AccordionSummary';
import AccordionDetails from '../../Accordion/AccordionDetails';
import ExpandMoreIcon from '../../../../public/images/expand-more.svg';
import iconStyles from '../../../theme/iconStyles.module.scss';
import styles from './styles.module.scss';

type ExpansionPanelClasses = {
  content?: string;
  summary?: string;
  detailsRoot?: string;
  icon?: string;
};

export type CustomizedExpansionPanelProps = {
  summary: React.ReactNode;
  content: React.ReactNode;
  hideSmall?: boolean;
  theme?: 'white' | 'grey';
  onChange?: (expanded: boolean) => void;
  defaultExpandedProp?: boolean;
  overrideExpanded?: boolean;
  classes?: ExpansionPanelClasses;
  expandIcon?: React.ReactNode;
  alwaysShowBorder?: boolean;
  title?: string;
  accordionLabelWrapper?: AccordionSummaryProps['wrapper'];
};

export default function CustomizedExpansionPanel({
  summary,
  content,
  theme = 'white',
  onChange,
  hideSmall,
  defaultExpandedProp = false,
  alwaysShowBorder = false,
  overrideExpanded,
  expandIcon,
  title = '',
  classes = {},
  accordionLabelWrapper = 'h3',
}: CustomizedExpansionPanelProps) {
  const baseID = useId();
  const [expanded, setExpanded] = useState(defaultExpandedProp);

  const summaryID = `${baseID}-summary`;
  const detailsID = `${baseID}-details`;

  useEffect(() => {
    if (overrideExpanded !== undefined) {
      setExpanded(overrideExpanded);
    }
  }, [overrideExpanded]);

  const handleChange = (_: Event, expanded: boolean) => {
    setExpanded(expanded);
    onChange?.(expanded);
  };

  return (
    <div className={styles.expansionContainer} data-testid={'expansion-container'}>
      <Accordion
        className={`${styles[theme]} ${styles.accordionCurvedBorder}`}
        classes={{
          root: styles.root,
          expanded: styles.expanded,
        }}
        onChange={handleChange}
        defaultExpanded={defaultExpandedProp}
        overrideExpanded={overrideExpanded}
      >
        <AccordionSummary
          wrapper={accordionLabelWrapper}
          id={summaryID}
          detailsID={detailsID}
          className={cx(azCommonStyles['az-body-1-heavy'], styles.expansionSummary, {
            [classes.summary || '']: classes.summary,
            [styles.expanded]: expanded && !alwaysShowBorder,
          })}
          contentClassName={classes.content}
          expandIcon={
            <Hidden className={styles.arrowExpand} smDown={hideSmall}>
              {expandIcon ? (
                expandIcon
              ) : (
                <ExpandMoreIcon
                  className={cx(iconStyles.defaultIconStyle, {
                    [classes.icon || '']: classes.icon,
                  })}
                />
              )}
            </Hidden>
          }
          title={title}
        >
          {summary}
        </AccordionSummary>
        {expanded && (
          <AccordionDetails
            classes={{
              root: cx(azCommonStyles['az-body-2-regular'], {
                [classes.detailsRoot || '']: classes.detailsRoot,
              }),
            }}
          >
            {content}
          </AccordionDetails>
        )}
      </Accordion>
    </div>
  );
}
